/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useRef, useState, useEffect } from 'react';
import { withStyles } from '@material-ui/core/styles';
import {
    Grid, TextField,
} from '@material-ui/core';
import {
    object, func, string, bool, shape,
} from 'prop-types';

const styles = (theme) => ({
    birth: {
        width: '250px',
        justifyContent: 'center',
        '& .age-verification': {
            padding: '0px !important',
        },
        '& .ageInputInvalidIcon': {
            padding: '0px 0px 16px 0px!important',
        },
    },
    cardTitleBirth: {
        fontSize: '18px',
        lineHeight: '23px',
        fontWeight: '700',
        margin: '16px 0px !important',
    },
    textField: {
        minHeight: '45px',
        margin: '0px 0px 0px 14px',
        '& .MuiInputBase-root': {
            backgroundColor: theme.palette.cms?.white || '#ffffff',
            boxShadow: ' 0 0 0 1px #C4C4C4',
            borderRadius: '5px',
            '&::before': {
                content: 'inherit',
            },
            '&::after': {
                content: 'inherit',
            },
            '& input': {
                padding: '25px 12px 8px !important',
                fontWeight: 100,
            },
        },
        '& .MuiFormLabel-root': {
            fontSize: '12px',
            color: '#555',
        },
        '& .MuiFormControl-marginDense': {
            margin: '0px',
        },
    },
    monthTextField: {
        margin: '0px !important',
    },
    ageFieldsInput: {
        flexWrap: 'nowrap',
    },
    ageVerification: {
        // keep minimal styles if you need pseudo-selectors for ::after/::before
        position: 'relative',
    },
    label: {
        fontSize: '12px',
        fontWeight: 500,
        marginBottom: '4px',
    },
    enterBirthInput: {
        '& input': {
            maxWidth: '48px',
        },
    },
    enterBirthYearInput: {
        '& input': {
            minWidth: '68px',
            maxWidth: '68px',
        },
    },
    errorFields: {
        '& .MuiInputBase-root': {
            boxShadow: '0 0 0 1px #DF3030',
        },
    },
});

const DateOfBirthContainer = ({
    classes,
    getBirthMonth,
    validateFields,
    birthMonth,
    monthErrorMessage,
    getBirthDay,
    birthDay,
    dayErrorMessage,
    getBirthYear,
    birthYear,
    yearErrorMessage,
    handleAgeVerification,
    inlinePDPAddon,
    label,
    type,
    setErrorState,
    dobErrorState,
}) => {
    const yearFieldRef = useRef(null);
    const monthFieldRef = useRef(null);
    const dayFieldRef = useRef(null);
    const numericOnly = /^[0-9]+$/;
    const [ageValidation, setAgeValidation] = useState({ flag: false, class: classes.ageInput, error: false });
    useEffect(() => {
    // Only run age verification when all fields are filled
        if (birthDay && birthMonth && birthYear?.length === 4) {
            const result = handleAgeVerification(type);
            setAgeValidation(result); // local state, no infinite loop
        } else {
            setAgeValidation({ flag: false, class: classes.ageInput, error: false });
        }
    }, [birthDay, birthMonth, birthYear, handleAgeVerification, type]);

    return (
        <div className={classes.birth}>
            <p className={classes.cardTitleBirth}>
                {label}
            </p>
            <Grid container direction="row" className={classes.ageFieldsInput}>
                <div className={`input-field ${classes.textField} ${classes.monthTextField}`}>
                    <TextField
                        inputRef={monthFieldRef}
                        margin="dense"
                        id="month"
                        label="Month"
                        variant="filled"
                        type="tel"
                        placeholder="MM"
                        onChange={(e) => {
                            getBirthMonth(e);
                            setErrorState(false);
                            if (e.target.value.length === 2 && e.target.value <= 12) {
                                if (birthDay.length === 2 && birthDay <= 31) {
                                    yearFieldRef.current.focus();
                                    return;
                                }
                                dayFieldRef.current.focus();
                            }
                        }}
                        className={((dobErrorState && !birthMonth) || ageValidation?.error) ? `${classes.enterBirthInput} ${classes.errorFields}` : classes.enterBirthInput}
                        onBlur={(e) => {
                            validateFields({ target: { value: !numericOnly.test(e.target.value) ? birthMonth : e.target.value, id: 'month' } });
                        }}
                        onKeyUp={(e) => {
                            validateFields({ target: { value: !numericOnly.test(e.target.value) ? birthMonth : e.target.value, id: 'month' } });
                        }}
                        value={birthMonth}
                        inputProps={{
                            'data-testid': 'month-field',
                            maxLength: 2,
                            pattern: '[0-9]/g',
                            min: 1,
                            max: 12,
                        }}
                        error={monthErrorMessage !== ''}
                        helperText={!inlinePDPAddon && monthErrorMessage}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                </div>
                <div className={`input-field ${classes.textField}`}>
                    <TextField
                        label="Day"
                        variant="filled"
                        margin="dense"
                        id="day"
                        inputRef={dayFieldRef}
                        type="tel"
                        placeholder="DD"
                        onChange={(e) => {
                            getBirthDay(e);
                            setErrorState(false);
                            if (e.target.value.length === 2 && e.target.value <= 31) {
                                yearFieldRef.current.focus();
                            }
                        }}
                        className={((dobErrorState && !birthDay) || ageValidation?.error) ? `${classes.enterBirthInput} ${classes.errorFields}` : classes.enterBirthInput}
                        onBlur={(e) => {
                            validateFields({
                                target: {
                                    value: !numericOnly.test(e.target.value) ? birthDay : e.target.value,
                                    id: 'day',
                                },
                            });
                        }}
                        onKeyUp={(e) => {
                            validateFields({ target: { value: !numericOnly.test(e.target.value) ? birthDay : e.target.value, id: 'day' } });
                        }}
                        value={birthDay}
                        inputProps={{
                            'data-testid': 'day-field',
                            maxLength: 2,
                            pattern: '[0-9]/g',
                            min: 1,
                            max: 31,
                        }}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        error={dayErrorMessage !== ''}
                        helperText={!inlinePDPAddon && dayErrorMessage}
                    />
                </div>
                <div className={`input-field ${classes.textField}`}>
                    <TextField
                        inputRef={yearFieldRef}
                        margin="dense"
                        id="year"
                        label="Year"
                        type="tel"
                        variant="filled"
                        placeholder="YYYY"
                        className={((dobErrorState && (!birthYear || birthYear?.length < 4)) || ageValidation?.error) ? `${classes.enterBirthYearInput} ${classes.errorFields}` : classes.enterBirthYearInput}
                        onKeyUp={(event) => {
                            validateFields(event);
                        }}
                        onChange={(event) => {
                            validateFields({ target: { value: !numericOnly.test(event.target.value) ? birthYear : event.target.value, id: 'year' } });
                            getBirthYear(event);
                            setErrorState(false);
                        }}
                        // onBlur={(e) => {
                        //     validateFields({ target: { value: !numericOnly.test(e.target.value) ? birthYear : e.target.value, id: 'year' } });
                        // }}
                        value={birthYear}
                        inputProps={{
                            maxLength: 4,
                            'data-testid': 'year-field',
                            pattern: '[0-9]/g',
                        }}
                        error={yearErrorMessage !== ''}
                        helperText={!inlinePDPAddon && yearErrorMessage}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                </div>

            </Grid>
            <div disabled={!ageValidation.flag} className={`age-verification ${ageValidation.class}`} />
            {dobErrorState && <span className={`${classes.errorMessage} ${classes.invalidZipError}`}>Enter a full date of birth</span>}
        </div>
    );
};

DateOfBirthContainer.defaultProps = {
    inlinePDPAddon: false,
    label: '',
    type: '',
};
DateOfBirthContainer.propTypes = {
    classes: object.isRequired,
    birthDay: string.isRequired,
    birthMonth: string.isRequired,
    birthYear: string.isRequired,
    dayErrorMessage: string.isRequired,
    monthErrorMessage: string.isRequired,
    yearErrorMessage: string.isRequired,
    getBirthDay: func.isRequired,
    getBirthMonth: func.isRequired,
    getBirthYear: func.isRequired,
    handleAgeVerification: func.isRequired,
    validateFields: func.isRequired,
    inlinePDPAddon: bool,
    label: string,
    type: string,
    setErrorState: func.isRequired,
    dobErrorState: func.isRequired,
};

export default withStyles(styles)(DateOfBirthContainer);
